import { forwardRef, useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

interface SelectOption {
  value: string
  label: string
  icon?: string
}

interface CustomSelectProps {
  label?: string
  value: string | undefined
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  error?: string
  helperText?: React.ReactNode
  required?: boolean
  disabled?: boolean
  className?: string
  /** "default" = h-14, "sm" = h-10 (para filtros/compacto) */
  size?: "default" | "sm"
  /** Mesmo fundo/borda dos FormInput (ex.: perfil) */
  variant?: "default" | "form"
  /** Foca o trigger ao montar (ex.: primeiro campo de um popup). */
  autoFocus?: boolean
}

type MenuLayout = {
  top: number
  left: number
  width: number
  position: "fixed"
  container: HTMLElement
  maxHeight: number
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>(function CustomSelect(
  {
    label,
    value,
    onChange,
    options,
    placeholder = "Selecione uma opção",
    error,
    helperText,
    required,
    disabled,
    className,
    size = "default",
    variant = "default",
    autoFocus = false,
  },
  forwardedRef,
) {
  const [isOpen, setIsOpen] = useState(false)
  const [menuLayout, setMenuLayout] = useState<MenuLayout | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const setTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      triggerRef.current = node
      if (typeof forwardedRef === "function") {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef],
  )

  const selectedOption = options.find((opt) => opt.value === value)

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return
    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - triggerRect.bottom - 8
    const spaceAbove = triggerRect.top - 8
    const preferBelow = spaceBelow >= spaceAbove
    const maxHeight = Math.min(240, Math.max(preferBelow ? spaceBelow : spaceAbove, 120))

    setMenuLayout({
      top: preferBelow ? triggerRect.bottom + 4 : triggerRect.top - maxHeight - 4,
      left: triggerRect.left,
      width: triggerRect.width,
      position: "fixed",
      container: document.body,
      maxHeight,
    })
  }, [])

  const toggleOpen = () => {
    if (disabled) return
    if (!isOpen) updateMenuPosition()
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    if (!isOpen) return

    const handleScroll = (e: Event) => {
      const target = e.target as Node | null
      if (target && menuRef.current?.contains(target)) return
      setIsOpen(false)
    }

    const handleResize = () => updateMenuPosition()

    window.addEventListener("scroll", handleScroll, true)
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("scroll", handleScroll, true)
      window.removeEventListener("resize", handleResize)
    }
  }, [isOpen, updateMenuPosition])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) setMenuLayout(null)
  }, [isOpen])

  useEffect(() => {
    if (!autoFocus || disabled) return
    const id = window.setTimeout(() => triggerRef.current?.focus(), 50)
    return () => window.clearTimeout(id)
  }, [autoFocus, disabled])

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault()
      if (!isOpen) updateMenuPosition()
      setIsOpen(true)
    }
    if (event.key === "Escape") setIsOpen(false)
  }

  return (
    <div className={cn("flex flex-col w-full", label ? "gap-2" : "", className)}>
      {label ? (
        <span
          className={cn(
            "font-bold text-foreground ml-1",
            variant === "form" || size !== "sm" ? "text-sm" : "text-xs")}
        >
          {label} {required && <span className="text-red-500">*</span>}
        </span>
      ) : null}
      
      <div
        ref={setTriggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleTriggerKeyDown}
        onClick={toggleOpen}
        className={cn(
          "relative w-full flex items-center rounded-xl transition-all cursor-pointer outline-none",
          size === "sm" ? "min-h-12 h-12 sm:h-10 px-4 sm:px-3 text-sm border" : "h-14 px-5 border",
          disabled
            ? "bg-muted/30 dark:bg-white/5 opacity-50 cursor-not-allowed border-border"
            : variant === "form"
              ? "bg-muted/30 dark:bg-black/20 border-border hover:border-border dark:hover:border-white/20"
              : "bg-card-surface border-border hover:border-primary/30",
          !disabled && variant !== "form" && isOpen && "border-primary/50 ring-2 ring-primary/10",
          !disabled && variant === "form" && isOpen && "border-primary/50",
          !disabled && !isOpen && "focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20",
          error ? "border-red-500" : "")}
      >
        <span
          className={cn(
            "min-w-0 w-full truncate text-left font-normal",
            !selectedOption ? "text-muted-foreground/50" : "text-foreground")}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
      </div>

      {error && (
        <p className="text-red-500 dark:text-red-400 text-sm font-medium ml-1">{error}</p>
      )}
      {helperText && !error ? (
        <p className="text-xs text-muted-foreground ml-1">{helperText}</p>
      ) : null}

      {isOpen && menuLayout && createPortal(
        <div
          ref={menuRef}
          onMouseDown={(e) => e.preventDefault()}
          style={{
            top: menuLayout.top,
            left: menuLayout.left,
            width: menuLayout.width,
            position: menuLayout.position,
            zIndex: 9999,
          }}
          className="pointer-events-auto overflow-hidden rounded-xl border border-border bg-card-surface shadow-2xl"
        >
          <div
            className="overflow-y-auto overscroll-contain p-1.5 pb-4"
            style={{ maxHeight: menuLayout.maxHeight }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {options.map((option) => (
              <div
                key={option.value}
                role="option"
                aria-selected={value === option.value}
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onChange(option.value)
                  setIsOpen(false)
                }}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 my-0.5 rounded-xl transition-all cursor-pointer group",
                  value === option.value 
                    ? "bg-primary/10 text-primary" 
                    : "text-muted-foreground dark:text-muted-foreground hover:bg-muted/30 dark:hover:bg-white/5 hover:text-primary"
                )}
              >
                {option.icon && (
                  <div className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full transition-all",
                    value === option.value 
                      ? "bg-primary text-white" 
                      : "bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                  )}>
                    <Icon name={option.icon} className="text-[18px]" />
                  </div>
                )}
                <span className="text-sm font-normal">{option.label}</span>
                {value === option.value && (
                  <Icon name="check" />
                )}
              </div>
            ))}
          </div>
        </div>,
        menuLayout.container
      )}
    </div>
  )
})

CustomSelect.displayName = "CustomSelect"
